import { makeAutoObservable } from 'mobx'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { PhoneInputStore } from 'shared/ui'
import { isCountryCode } from 'shared/lib'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowForwardCallStepNumber } from 'entities/SmartCall/api/types'
import { type Phone } from 'entities/Phone/model/Phone'
import type { IIWorkFlowSendFromBlockArgs } from 'entities/Workflow'
import { SendFromBlockStore } from 'widgets/Flow/blocks/SendFrom'
import { NumberWhisperStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/ForwardCall/ui/ForwardCallActionEditContent/NumberSetting/NumberWhisper/store/NumberWhisperStore'

export class NumberSettingStore {
  readonly externalPhoneInputStore: PhoneInputStore
  constructor(
    readonly entities: IEntitiesStore,
    readonly phone: Phone,
    readonly initConfig?: ISmartCallFlowForwardCallStepNumber
  ) {
    this._forwardTo = initConfig?.forward_to || null
    const inboxArgs: IIWorkFlowSendFromBlockArgs | null =
      initConfig?.forward_to === 'inbox'
        ? {
            inboxId: initConfig.forwardOptions.inbox_id,
            sendingOptions: {
              number_id: initConfig.forwardOptions.number_id,
            },
          }
        : null
    this.sendFromBlockStore = new SendFromBlockStore(this.entities, inboxArgs, {
      excludeDeprioritizedNumbers: true,
      getDropdownItemsOptions: {
        excludeSendingOptions: true,
        showOnlyVerifiedToolFree: true,
        excludeNumbersIds: [phone.id],
      },
    })
    this.externalPhoneInputStore = new PhoneInputStore({
      validate: (number, country) => {
        if (!number.trim()) return false
        if (!isCountryCode(country.id)) return false
        return isValidPhoneNumber(number, country.id)
      },
    })
    if (initConfig?.forward_to === 'externalNumber') {
      if (initConfig.forwardOptions.external_number)
        this.externalPhoneInputStore.setFullNumber(initConfig.forwardOptions.external_number)
      this._extension = initConfig.forwardOptions.extension || ''
    }

    this.numberWhisperStore = new NumberWhisperStore(this.entities, initConfig?.whisperConfig)
    this._screening = initConfig?.screening || false
    makeAutoObservable(this)
  }

  get params(): ISmartCallFlowForwardCallStepNumber {
    const commonParams: Pick<
      ISmartCallFlowForwardCallStepNumber,
      'number_id' | 'screening' | 'whisperConfig'
    > = {
      number_id: this.phone.id,
      screening: this.screening,
      whisperConfig: this.numberWhisperStore.params,
    }
    if (this.forwardTo === 'inbox') {
      return {
        ...commonParams,
        forward_to: 'inbox',
        forwardOptions: {
          inbox_id: this.sendFromBlockStore.params.inboxId,
          number_id: this.sendFromBlockStore.params.sendingOptions?.number_id || null,
        },
      }
    }
    if (this.forwardTo === 'externalNumber') {
      return {
        ...commonParams,
        forward_to: 'externalNumber',
        forwardOptions: {
          external_number: this.externalPhoneInputStore.fullUnformattedNumber,
          extension: this.extension,
        },
      }
    }
    return {
      ...commonParams,
      forward_to: null,
      forwardOptions: null,
    }
  }

  validate = () => {
    let valid = true
    if (!this.params.forward_to) {
      this.setForwardToError('Required field')
      valid = false
    }
    if (this.params.forward_to === 'externalNumber') {
      this.externalPhoneInputStore.triggerNumberValidation()
      if (!this.externalPhoneInputStore.isValidNumber) {
        valid = false
      }
    }
    if (this.params.forward_to === 'inbox') {
      if (!this.params.forwardOptions.number_id) {
        this.sendFromBlockStore.setError('Select an inbox to continue')
        valid = false
      }
    }
    if (!this.numberWhisperStore.validate()) {
      valid = false
    }

    return valid
  }

  get isConfigured() {
    return (
      (this.params.forward_to === 'inbox' && !!this.params.forwardOptions.number_id) ||
      (this.params.forward_to === 'externalNumber' &&
        !!this.params.forwardOptions.external_number) ||
      false
    )
  }

  onDisable = () => {
    this._forwardTo = null
    this.sendFromBlockStore.reset()
    this.numberWhisperStore.reset()
    this._screening = false
    this.externalPhoneInputStore.setNumber('')
    this._extension = ''
  }

  get forwardToLabel() {
    if (this.params.forward_to === 'inbox') {
      return (
        this.entities.numbersStore.getItem(this.params.forwardOptions.number_id)
          ?.formatted_number || ''
      )
    }
    if (this.params.forward_to === 'externalNumber') {
      return this.externalPhoneInputStore.number
    }
    return ''
  }

  get isConfigurationError(): boolean {
    return Boolean(
      this.forwardToError ||
        this.externalNumberError ||
        this.sendFromBlockStore.error ||
        this.numberWhisperStore.error
    )
  }

  private _forwardTo: ISmartCallFlowForwardCallStepNumber['forward_to'] = null
  get forwardTo() {
    return this._forwardTo
  }
  setForwardTo = (value: typeof this.forwardTo) => {
    this.setForwardToError('')
    this.sendFromBlockStore.setError('')
    this.setExternalNumberError('')
    this._forwardTo = value
  }
  private _forwardToError = ''
  get forwardToError() {
    return this._forwardToError
  }
  setForwardToError = (value: typeof this._forwardToError) => {
    this._forwardToError = value
  }

  readonly sendFromBlockStore: SendFromBlockStore

  get externalNumberError() {
    return (
      this.externalPhoneInputStore.numberError || this.externalPhoneInputStore.numberRequestError
    )
  }
  setExternalNumberError = (value: string) => {
    this.externalPhoneInputStore.setNumberError(value)
  }

  private _extension = ''
  get extension() {
    return this._extension
  }
  setExtension = (value: string) => {
    this._extension = value
  }

  readonly numberWhisperStore: NumberWhisperStore

  private _screening
  get screening() {
    return this._screening
  }
  setScreening = (value: boolean) => {
    this._screening = value
  }
}
